import { useEffect, useRef, useState } from "react";
import { Clock3, Pause } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useToast } from "@/hooks/use-toast";
import { canTrackTimeTrackTarget, openTimeTrack, useTimeTrackAccess, useTimeTrackMutation, useTimeTrackOnline, useTimeTrackState, useTimeTrackTargets } from "@/hooks/use-timetrack";
import type { TargetType } from "@/hooks/use-timetrack";
import "./timetrack.css";

export interface TimeTrackButtonProps {
  targetType: TargetType;
  targetId: string | number;
  projectId: string;
  className?: string;
  label?: string;
}

/** Click starts/resumes; a sustained hover reveals time options without starting a timer. */
export function TimeTrackButton({ targetType, targetId, projectId, className = "", label }: TimeTrackButtonProps) {
  const { enabled } = useTimeTrackAccess();
  return enabled ? <InternalTimeTrackButton targetType={targetType} targetId={targetId} projectId={projectId} className={className} label={label} /> : null;
}

function InternalTimeTrackButton({ targetType, targetId, projectId, className = "", label }: TimeTrackButtonProps) {
  const targets = useTimeTrackTargets(false);
  const canTrack = canTrackTimeTrackTarget(targets.data, projectId, targetType, targetId);
  return canTrack ? <AuthorizedTimeTrackButton targetType={targetType} targetId={targetId} projectId={projectId} className={className} label={label} /> : null;
}

function AuthorizedTimeTrackButton({ targetType, targetId, projectId, className = "", label }: TimeTrackButtonProps) {
  const state = useTimeTrackState(false);
  const mutation = useTimeTrackMutation();
  const online = useTimeTrackOnline();
  const { toast } = useToast();
  const [menuOpen, setMenuOpen] = useState(false);
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelHover = () => {
    if (hoverTimer.current !== null) clearTimeout(hoverTimer.current);
    hoverTimer.current = null;
  };
  useEffect(() => cancelHover, []);
  const running = state.data?.running;
  const active = running?.targetType === targetType && running.targetId === String(targetId);
  const paused = state.data?.paused.some(e => e.targetType === targetType && e.targetId === String(targetId));
  return <span className={`tt tt-inline ${className}`} onClick={e => e.stopPropagation()} onDoubleClick={e => e.stopPropagation()}>
    <DropdownMenu open={menuOpen} onOpenChange={open => { cancelHover(); setMenuOpen(open); }} modal={false}>
    <DropdownMenuTrigger asChild>
    <button type="button" className={`tt-button tt-icon ${active ? "tt-active" : ""}`}
      aria-disabled={!online || mutation.isPending}
      aria-label={active ? "Tracking this item — review time" : paused ? "Continue tracking this item" : "Start tracking this item"}
      title="Click to track time. Hover for 1.5 seconds or press Arrow Down for time options."
      onPointerEnter={e => {
        if (e.pointerType === "touch") return;
        cancelHover();
        hoverTimer.current = setTimeout(() => { hoverTimer.current = null; setMenuOpen(true); }, 1500);
      }}
      onPointerLeave={cancelHover}
      onBlur={cancelHover}
      onPointerDown={e => { cancelHover(); e.preventDefault(); e.currentTarget.focus(); }}
      onKeyDown={e => {
        // Radix normally opens on Enter/Space; keep those as the primary click action.
        if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.currentTarget.click(); }
      }}
      onClick={() => {
        cancelHover();
        if (menuOpen || !online || mutation.isPending) return;
        if (active) { openTimeTrack({ view: "project", projectId, targetType, targetId }); return; }
        mutation.mutate({ action: "start", data: { targetType, targetId: String(targetId) } }, {
          onError: error => toast({ title: "Time change not confirmed", description: error.message, variant: "destructive" }),
        });
      }}>
      {paused && !active ? <Pause size={14} /> : <Clock3 size={14} />}{label}
    </button>
    </DropdownMenuTrigger>
      <DropdownMenuContent className="tt-inline-menu" align="end">
        <DropdownMenuItem onSelect={() => openTimeTrack({ view: "manual", projectId, targetType, targetId })}>Add manual time</DropdownMenuItem>
        <DropdownMenuItem onSelect={() => openTimeTrack({ view: "project", projectId, targetType, targetId })}>Review item time</DropdownMenuItem>
        <DropdownMenuItem onSelect={() => openTimeTrack({ view: "my" })}>My Time</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  </span>;
}
